import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';
// import OHIF from '@ohif/core';
import { SimpleDialog } from '@ohif/ui';
import { Spin, message } from 'antd';
import { servicesManager } from '../../App';
import useSearchParams from '../../customHooks/useSearchParams';
import * as api from './api';
import './markList.less';
import './form.css';
import BaseForm from './baseForm';
import {
  setActiveMarkUUID,
  setPagination,
  setMarkState,
} from '@ohif/viewer/src/store/mark/action';
import { MARK_STATE } from '@ohif/viewer/src/store/mark/types';

export const FormView = () => {
  const { activeButtonIds, viewports, mark } = useSelector(
    ({ viewer: { activeButtonIds }, viewports, mark }) => ({
      viewports,
      activeButtonIds,
      mark,
    })
  );
  const [{ aiCheckId: caseId, pro: imageDataId }] = useSearchParams();
  const { SidePanelService, LoggerService } = servicesManager.services;
  const { toolType, uuid, form: properties = {}, id } = SidePanelService.state;
  if (!caseId || !toolType || !uuid)
    throw new Error('缺少caseID|toolType|uuid');

  const [form, setForm] = useState(properties);
  // 获取视图元素
  const activeIndex = viewports.activeViewportIndex;
  const element = cornerstoneTools.store.state.enabledElements[activeIndex];
  // 获取数据
  const toolStateManager = cornerstoneTools.getToolState(element, toolType);
  // 未找到绘图数据时返回列表
  if (!toolStateManager) {
    message.error('未找到标注数据!');
    SidePanelService.handleSidePanelChange('right', 'mark-list');
  }
  const measurementData = toolStateManager
    ? toolStateManager.data.find(item => item.uuid === uuid)
    : {};
  const dispatch = useDispatch();
  const ref = useRef();
  const onCancel = useCallback(() => {
    cornerstoneTools.removeToolState(element, toolType, measurementData);
    cornerstone.updateImage(element);
    SidePanelService.handleSidePanelChange('right', 'mark-list');
  }, [SidePanelService, element, measurementData, toolType]);
  const [loading, setLoading] = useState(false);
  const onSubmit = useCallback(async () => {
    try {
      await ref.current.form.validateFields();
    } catch (errorInfo) {
      return;
    }

    const enabledElement = cornerstone.getEnabledElement(element);
    let matchData =
      enabledElement.image.imageId.match(
        /(studyUID|seriesUID|objectUID)=[\d|.]*/g
      ) || [];
    matchData = matchData.map(v => v.replace(/\w+=/, ''));
    const [StudyInstanceUID, SeriesInstanceUID, SOPInstanceUID] = matchData;

    const generalImageModule =
      cornerstone.metaData.get(
        'generalImageModule',
        enabledElement.image.imageId
      ) || {};
    const { instanceNumber } = generalImageModule;

    const seriesMetadata =
      cornerstone.metaData.get(
        'generalSeriesModule',
        enabledElement.image.imageId
      ) || {};
    const { seriesNumber } = seriesMetadata;

    const { windowWidth, windowCenter } = enabledElement.viewport.voi;

    let params = {
      caseId,
      imageDataId,
      ser: seriesNumber,
      img: instanceNumber,
      serUid: SeriesInstanceUID,
      sopUid: SOPInstanceUID,
      studyUid: StudyInstanceUID,
      dataUuid: measurementData.uuid,
      areaData: JSON.stringify(measurementData),
      source: 1,
      type: toolType,
      data: JSON.stringify(form),
      windowType: JSON.stringify({
        buttonId: activeButtonIds[0],
        windowWidth,
        windowCenter,
      }),
      tagResultDto: {
        size: mark.pagination.pageSize,
        accountIds: mark.markUsers.join(','),
      },
    };
    setLoading(true);
    let requestApi = 'createMark';
    if (id) {
      params.id = id;
      requestApi = 'updateMark';
    }
    api[requestApi](params)
      .then(res => {
        if (!id) {
          typeof res.data === 'object' &&
            dispatch(setPagination({ current: res.data.current + 1 }));
        }

        SidePanelService.handleSidePanelChange('right', 'mark-list');
      })
      .catch(error => LoggerService.error({ error }))
      .finally(() => {
        setLoading(false);
        dispatch(setActiveMarkUUID(measurementData.uuid));
      });
  }, [
    LoggerService,
    SidePanelService,
    activeButtonIds,
    caseId,
    dispatch,
    element,
    form,
    id,
    imageDataId,
    mark.markUsers,
    mark.pagination.pageSize,
    measurementData,
    toolType,
  ]);
  useEffect(() => {
    // 绘制图形后禁止再次绘制
    dispatch(setMarkState([MARK_STATE.ENABLED_CREATE]));
    return () => {
      dispatch(setMarkState());
    };
  }, [dispatch]);
  return (
    <div className="mark-list">
      <h1 className="viewer-mark-title mark-bg">编辑标注</h1>
      <Spin spinning={loading}>
        {/* <App></App> */}
        <BaseForm model={form} onChange={setForm} ref={ref}>
          {/* {form.lesion && (
            <div className="action">
              <button className="btn btn-sm btn-default" onClick={onCancel}>
                取消
              </button>
              <button className="btn btn-sm btn-primary" onClick={onSubmit}>
                确定
              </button>
            </div>
          )} */}
          <div className="action">
            <button className="btn btn-sm btn-default" onClick={onCancel}>
              取消
            </button>
            <button className="btn btn-sm btn-primary" onClick={onSubmit}>
              确定
            </button>
          </div>
        </BaseForm>
      </Spin>
    </div>
  );
};

const DialogForm = ({ onConfirm, ...otherProps }) => {
  const [form, setForm] = useState({});
  const handleConfirm = useCallback(() => onConfirm(form), [form, onConfirm]);
  return (
    <SimpleDialog {...otherProps} onConfirm={handleConfirm}>
      <BaseForm onChange={setForm}></BaseForm>
    </SimpleDialog>
  );
};

DialogForm.propTypes = SimpleDialog.propTypes;

export const handleDialogForm = ({ viewports }, type) => {
  const { UIDialogService } = servicesManager.services;
  const activeIndex = viewports.activeViewportIndex;
  const el = cornerstoneTools.store.state.enabledElements[activeIndex];
  // 获取数据
  const toolStateManager = cornerstoneTools.getToolState(el, type);
  if (UIDialogService) {
    let dialogId = UIDialogService.create({
      centralize: true,
      isDraggable: false,
      content: DialogForm,
      useLastPosition: false,
      showOverlay: true,
      contentProps: {
        headerTitle: '标注输入',
        onClose: () => {
          UIDialogService.dismiss({ id: dialogId });
          const data = toolStateManager.data[toolStateManager.data.length - 1];
          cornerstoneTools.removeToolState(el, type, data);
          cornerstone.updateImage(el);
        },
        onConfirm: value => {
          UIDialogService.dismiss({ id: dialogId });
        },
      },
    });
  }
};

const FormViewWrap = () => {
  const [{ aiCheckId: caseId }] = useSearchParams();
  const { SidePanelService } = servicesManager.services;
  const { toolType, uuid } = SidePanelService.state;
  return !caseId || !toolType || !uuid ? (
    <div>缺少caseID|toolType|uuid</div>
  ) : (
    <FormView />
  );
};

export default FormViewWrap;
